import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import LogoLoading from '@/dashboard/components/loading/LogoLoading';
import { useAuth0 } from '@auth0/auth0-react';
import { api, useAuthStore } from '@jove-frontend/uikit';

export const withAuthRequired = (WrappedComponent: React.ComponentType) => {
  const ComponentWithAuthRequired = (props: Record<string, unknown>) => {
    const router = useRouter();
    const { user, isAuthenticated, loginWithRedirect, getAccessTokenSilently, logout } = useAuth0();
    const [{ user: authenticatedUser }, { setUserRoles, fetchUser }] = useAuthStore(({ state, actions }) => [state, actions]);

    const [isApiAuthorized, setIsApiAuthorized] = useState(false);

    const getToken = useCallback(async () => {
      try {
        const res = await getAccessTokenSilently({ detailedResponse: true });

        api.defaults.headers.common.Authorization = `Bearer ${res.id_token}`;
        setIsApiAuthorized(!!api.defaults.headers.common.Authorization);
      } catch (_) {
        // Session was lost abnormally (e.g. storage cleared manually): follow the same flow as logout
        // instead of staying on the loading view forever
        await logout();
      }
    }, [getAccessTokenSilently, logout]);

    useEffect(() => {
      if (!isAuthenticated) {
        loginWithRedirect({
          appState: {
            returnTo: router.asPath,
          },
        });
      } else if (!isApiAuthorized) {
        getToken();
      } else {
        setUserRoles(user?._roles);
        fetchUser(user.sub);
      }
    }, [router, isApiAuthorized, isAuthenticated, getToken, loginWithRedirect]);

    if (!isApiAuthorized || !authenticatedUser) {
      return <LogoLoading />;
    }

    return <WrappedComponent {...props} />;
  };

  return ComponentWithAuthRequired;
};
